import React, { useState, useEffect } from 'react';
import { Box, Button, styled, Avatar, Menu, MenuItem, ListItemIcon, ListItemText, Typography, CircularProgress, Divider } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Home as HomeIcon,
  BarChart as BarChartIcon,
  TrackChanges as TargetIcon,
  AutoAwesome as SparklesIcon,
  ChatBubbleOutline as MessageSquareIcon,
  FlashOn as ZapIcon,
  Article as ArticleIcon,
  Settings as SettingsIcon,
  Security as ShieldIcon,
  KeyboardArrowDown as ChevronDownIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Check as CheckIcon,
  Logout as LogoutIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import { useTheme } from '../../globalContexts/ThemeContext';
import { userProfile, clearCacheData } from 'utils/auth';
import userApiService from 'app/apiService/user/userApiService';
import logoBlackImage from '../../assets/images/logo_black.png';
import logoWhiteImage from '../../assets/images/logo_white.png';
import { selectDealerSelected, selectDealersList } from '../../redux/headerSlice/selectors';
import { headerActions } from '../../redux/headerSlice/slice';
import DealerService from '../../services/apiService/dealerApiService';
import { getMaterialIconComponent } from 'app/components/MaterialIconMap';
import { DebugPanelMAIQ } from 'app/components/ReusableUIComponents/HighLevelComponents/DebugPanelMAIQ';
import { DEBUG_CONFIG } from 'app/components/ReusableUIComponents/HighLevelComponents/DebugPanelMAIQ/config';
import { shouldShowMockIndicator } from 'app/apiService/miaChat/config';

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 256,
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
}));

const DealershipSelector = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DealerLogoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 80,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

const DealerLogoImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
});

const DealershipButton = styled(Button)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const Navigation = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const NavButton = styled(Button)<{ active?: boolean }>(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  justifyContent: 'flex-start',
  fontWeight: 500,
  transition: 'all 0.2s ease-in-out',
  ...(active
    ? {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          opacity: 0.9,
        },
      }
    : {
        color: theme.palette.text.secondary,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.primary,
        },
      }),
}));

const AdminSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const ProfileSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ProfileButton = styled(Button)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  justifyContent: 'flex-start',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor: theme.palette.primary.main,
  fontSize: '1rem',
  fontWeight: 600,
}));

const UserInfo = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

const UserName = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.2,
  textAlign: 'left',
}));

const UserRole = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.2,
  textAlign: 'left',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'center',
}));

const LogoImage = styled('img')({
  width: '100%',
  height: 50,
  objectFit: 'contain',
});

// Type for navigation items from API
type NavigationItemFromAPI = {
  label: string;
  link: string;
  icon: string;
};

// Type for navigation items in component
type NavigationItem = {
  name: string;
  icon: React.ComponentType<any>;
  href: string;
};

// Function to get sidebar navigation mappings from user_token
function getSidebarNavigationMappings(): NavigationItemFromAPI[] {
  try {
    const token = localStorage.getItem('user_token');
    if (!token) {
      console.warn('user_token not found in localStorage');
      return [];
    }
    
    const parsed = JSON.parse(token);
    // If the token is a JWT, decode the payload
    let mappings = [];
    if (typeof parsed === 'string' && parsed.split('.').length === 3) {
      const payload = JSON.parse(atob(parsed.split('.')[1]));
      mappings = payload.sidebar_navigation_mappings || [];
    } else {
      mappings = parsed.sidebar_navigation_mappings || [];
    }
    
    return Array.isArray(mappings) ? mappings : [];
  } catch (error) {
    console.error('Error parsing sidebar_navigation_mappings from user_token:', error);
    return [];
  }
}

// Function to check if an item is an admin item (Settings or Admin)
function isAdminItem(link: string): boolean {
  const normalizedLink = link?.toLowerCase().trim() || '';
  return normalizedLink.includes('/settings') || normalizedLink.includes('/admin');
}

// Function to map API navigation items to component navigation items
function mapNavigationItems(apiItems: NavigationItemFromAPI[]): NavigationItem[] {
  return apiItems.map((item) => {
    // Get icon component from MaterialIconMap
    const iconElement = getMaterialIconComponent(item.icon);
    
    // Create a wrapper component that renders the icon element
    // If icon not found, use HomeIcon as fallback
    const IconComponent: React.ComponentType<any> = iconElement 
      ? (props: any) => React.cloneElement(iconElement, props)
      : HomeIcon;
    
    return {
      name: item.label || 'Unknown',
      icon: IconComponent,
      href: item.link || '#',
    };
  });
}

// Function to get admin items from API (Settings and Admin)
function getAdminItemsFromAPI(): NavigationItem[] {
  try {
    const apiItems = getSidebarNavigationMappings();
    if (apiItems.length === 0) {
      return [];
    }
    
    // Filter only admin items (Settings and Admin)
    const adminApiItems = apiItems.filter(item => isAdminItem(item.link || ''));
    
    // Map to navigation items
    return mapNavigationItems(adminApiItems);
  } catch (error) {
    console.error('Error getting admin items from API:', error);
    return [];
  }
}

// Function to get main navigation items (excluding admin items)
function getMainNavigationItems(apiItems: NavigationItemFromAPI[]): NavigationItem[] {
  // Filter out admin items (Settings and Admin)
  const mainItems = apiItems.filter(item => !isAdminItem(item.link || ''));
  return mapNavigationItems(mainItems);
}

// Fallback navigation items if no mappings found
const fallbackNavigationItems: NavigationItem[] = [
  { name: 'Home', icon: HomeIcon, href: '/ai-avatar-layout' },
  { name: 'Measure', icon: BarChartIcon, href: '/ai-avatar-layout/measure' },
  { name: 'Target', icon: TargetIcon, href: '/ai-avatar-layout/target' },
  { name: 'Generate', icon: SparklesIcon, href: '/ai-avatar-layout/generate' },
  { name: 'Analyze', icon: MessageSquareIcon, href: '/ai-avatar-layout/analyze' },
];

// Get initial navigation items from user_token (excluding admin items)
const getInitialNavigationItems = (): NavigationItem[] => {
  const apiNavigationItems = getSidebarNavigationMappings();
  return apiNavigationItems.length > 0 
    ? getMainNavigationItems(apiNavigationItems)
    : fallbackNavigationItems;
};

// Get initial admin items from user_token
const getInitialAdminItems = (): NavigationItem[] => {
  return getAdminItemsFromAPI();
};

export const Sidebar: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { mode, toggleTheme } = useTheme();
  
  // Get dealers list and selected dealer from Redux
  const dealersList = useSelector(selectDealersList);
  const selectedDealer = useSelector(selectDealerSelected);
  
  const [dealerMenuAnchor, setDealerMenuAnchor] = useState<null | HTMLElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loadingDealers, setLoadingDealers] = useState(false);
  const [dynamicNavigationItems, setDynamicNavigationItems] = useState<NavigationItem[]>(getInitialNavigationItems());
  const [dynamicAdminItems, setDynamicAdminItems] = useState<NavigationItem[]>(getInitialAdminItems());
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const dealerMenuOpen = Boolean(dealerMenuAnchor);
  const menuOpen = Boolean(anchorEl);
  
  // Update navigation items when user_token changes
  useEffect(() => {
    const updateNavigationItems = () => {
      try {
        const apiItems = getSidebarNavigationMappings();
        if (apiItems.length > 0) {
          // Get main navigation items (excluding admin items)
          const mainItems = getMainNavigationItems(apiItems);
          setDynamicNavigationItems(mainItems);
          
          // Get admin items separately
          const adminItems = getAdminItemsFromAPI();
          setDynamicAdminItems(adminItems);
        } else {
          // Use fallback items if no mappings found
          setDynamicNavigationItems(fallbackNavigationItems);
          setDynamicAdminItems([]); // No admin items in fallback
        }
      } catch (error) {
        console.error('Error updating navigation items:', error);
        // Use fallback items on error
        setDynamicNavigationItems(fallbackNavigationItems);
        setDynamicAdminItems([]);
      }
    };
    
    // Initial load
    updateNavigationItems();
    
    // Listen for storage changes (in case user_token is updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user_token') {
        updateNavigationItems();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const user = userProfile(false);
  const userName = user?.name || user?.email || 'User';
  const userInitials = userName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  
  // Function to format role name for display
  const formatRoleName = (role: string | string[] | undefined): string => {
    // Handle undefined or empty
    if (!role) return 'User';
    
    // Handle array - take the first role
    let roleString: string;
    if (Array.isArray(role)) {
      if (role.length === 0) return 'User';
      roleString = role[0];
    } else {
      roleString = role;
    }
    
    // Role mapping for common roles
    const roleDisplayMap: Record<string, string> = {
      'beta_maiq_performance_manager': 'Performance Manager',
      'maiq_performance_manager': 'Performance Manager',
      'dealership_executive': 'Dealership Executive',
      'dealership_group_executive': 'Dealership Group Executive',
      'maiq_super_admin': 'Super Admin',
      'super_user': 'Super User',
      'maiq_tech_user': 'Tech User',
      'maiq_sales_user': 'Sales User',
      'sales_person': 'Sales Person',
      'maiq_content_manager': 'Content Manager',
    };
    
    // Check if role exists in mapping
    if (roleDisplayMap[roleString]) {
      return roleDisplayMap[roleString];
    }
    
    // Fallback: convert snake_case to Title Case
    return roleString
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  // Get user role from user_token
  const userRole = formatRoleName(user?.roles);
  
  // Get current dealership name
  const currentDealerName = selectedDealer?.site_title || selectedDealer?.name || 'Select Dealership';
  
  // Fetch dealers on component mount
  useEffect(() => {
    const fetchDealers = async () => {
      // Check if dealers already loaded
      if (dealersList?.data?.body?.items?.length > 0) {
        return;
      }
      
      setLoadingDealers(true);
      try {
        const response = await DealerService.getAllDealers();
        if (response.body?.items) {
          // Sort dealers: active first, then alphabetically
          const sortedDealers = response.body.items
            .map((item: any) => ({
              ...item,
              dealer: item._id || "",
              site_title: item.name || ""
            }))
            .sort((a: any, b: any) => {
              if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') {
                return 1;
              } else if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') {
                return -1;
              } else {
                return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
              }
            });
          
          dispatch(headerActions.requestDealersSuccess({
            ...response,
            body: { ...response.body, items: sortedDealers }
          }));
        }
      } catch (error) {
        console.error('Error loading dealers:', error);
      } finally {
        setLoadingDealers(false);
      }
    };

    fetchDealers();
  }, [dispatch, dealersList]);

  // Handle dealer selection from localStorage (similar to Header component)
  useEffect(() => {
    const handleDealerFromLocalStorage = () => {
      // Only proceed if dealers are loaded and not currently loading
      if (dealersList?.data?.body?.items?.length > 0 && !loadingDealers) {
        try {
          // Get dealer from localStorage
          const storedDealer = localStorage.getItem('dealer');
          let dealer = storedDealer ? JSON.parse(storedDealer) : {};
          
          // Get the dealer ID from localStorage
          const currentDealerId = Object.keys(dealer).length 
            ? dealer.dealer || dealer._id 
            : 'default';

          // Get the currently selected dealer ID
          const selectedDealerId = selectedDealer?._id || selectedDealer?.dealer || selectedDealer;

          // If localStorage dealer is different from selected dealer, update it
          if (currentDealerId !== selectedDealerId) {
            if (currentDealerId === 'default') {
              // Select first active dealer if no dealer in localStorage
              const firstActiveDealer = dealersList.data.body.items.find((d: any) => d.status === 'ACTIVE');
              if (firstActiveDealer) {
                dispatch(headerActions.selectDealer(firstActiveDealer));
                localStorage.setItem('dealer', JSON.stringify(firstActiveDealer));
              }
            } else {
              // Find and select the dealer from localStorage
              const dealerFromStorage = dealersList.data.body.items.find(
                (item: any) => item._id === currentDealerId || item.dealer === currentDealerId
              );
              
              if (dealerFromStorage) {
                dispatch(headerActions.selectDealer(dealerFromStorage));
              } else {
                // If dealer from localStorage not found, select first active dealer
                const firstActiveDealer = dealersList.data.body.items.find((d: any) => d.status === 'ACTIVE');
                if (firstActiveDealer) {
                  dispatch(headerActions.selectDealer(firstActiveDealer));
                  localStorage.setItem('dealer', JSON.stringify(firstActiveDealer));
                }
              }
            }
          }
        } catch (error) {
          console.error('Error loading dealer from localStorage:', error);
          // On error, select first active dealer
          const firstActiveDealer = dealersList.data.body.items.find((d: any) => d.status === 'ACTIVE');
          if (firstActiveDealer) {
            dispatch(headerActions.selectDealer(firstActiveDealer));
            localStorage.setItem('dealer', JSON.stringify(firstActiveDealer));
          }
        }
      }
    };

    handleDealerFromLocalStorage();
  }, [dispatch, dealersList, loadingDealers, selectedDealer]);

  const isActive = (href: string) => location.pathname === href;

  const handleNavigation = (href: string) => {
    history.push(href);
  };

  const handleDealerMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDealerMenuAnchor(event.currentTarget);
  };

  const handleDealerMenuClose = () => {
    setDealerMenuAnchor(null);
  };

  const handleDealerSelect = (dealer: any) => {
    // Save to localStorage
    localStorage.setItem('dealer', JSON.stringify(dealer));
    // Update Redux state
    dispatch(headerActions.selectDealer(dealer));
    handleDealerMenuClose();
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleThemeChange = (newMode: 'light' | 'dark') => {
    if (mode !== newMode) {
      toggleTheme();
    }
    handleMenuClose();
  };

  const handleLogout = async () => {
    try {
      await userApiService.postLogOutEvent();
      window.localStorage.removeItem('user_token');
      // Remove theme on logout
      localStorage.removeItem('app-theme');
      ['dealer', 'user_token'].forEach(key => localStorage.removeItem(key));
      clearCacheData();
      window.location.href = '/login';
    } catch (error) {
      console.log('logout error', error);
    } finally {
      window.localStorage.removeItem('user_token');
      // Remove theme on logout
      localStorage.removeItem('app-theme');
      ['dealer', 'user_token'].forEach(key => localStorage.removeItem(key));
      clearCacheData();
      window.location.href = '/login';
    }
  };

  return (
    <SidebarContainer>
      {/* Dealership Selector */}
      <DealershipSelector>
        {/* Dealer Logo */}
        {selectedDealer?.logo_url && (
          <DealerLogoContainer key={selectedDealer._id || selectedDealer.dealer}>
            <DealerLogoImage 
              src={selectedDealer.logo_url} 
              alt={currentDealerName}
              onError={(e) => {
                // Hide image on error
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </DealerLogoContainer>
        )}
        
        <DealershipButton onClick={handleDealerMenuOpen} disabled={loadingDealers}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            {loadingDealers ? (
              <CircularProgress size={20} />
            ) : (
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                {currentDealerName
                  .split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </Avatar>
            )}
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1
              }}
            >
              {currentDealerName}
            </Typography>
          </Box>
          <ChevronDownIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
        </DealershipButton>
        
        {/* Dealers Menu */}
        <Menu
          anchorEl={dealerMenuAnchor}
          open={dealerMenuOpen}
          onClose={handleDealerMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 240,
              maxHeight: 400,
              mt: 1,
            },
          }}
        >
          {dealersList?.data?.body?.items?.map((dealer: any) => (
            <MenuItem
              key={dealer._id || dealer.dealer}
              onClick={() => handleDealerSelect(dealer)}
              selected={
                selectedDealer?.dealer === dealer._id || 
                selectedDealer?._id === dealer._id ||
                selectedDealer?.dealer === dealer.dealer
              }
              disabled={dealer.status !== 'ACTIVE'}
              sx={{
                opacity: dealer.status !== 'ACTIVE' ? 0.5 : 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                  {(dealer.name || dealer.site_title || 'D')
                    .split(' ')
                    .map((n: string) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {dealer.name || dealer.site_title}
                  </Typography>
                }
                secondary={dealer.status !== 'ACTIVE' ? 'Inactive' : null}
              />
              {(selectedDealer?.dealer === dealer._id || 
                selectedDealer?._id === dealer._id ||
                selectedDealer?.dealer === dealer.dealer) && (
                <CheckIcon sx={{ fontSize: 18, color: 'primary.main', ml: 1 }} />
              )}
            </MenuItem>
          ))}
          
          {(!dealersList?.data?.body?.items || dealersList.data.body.items.length === 0) && (
            <MenuItem disabled>
              <ListItemText primary="No dealers available" />
            </MenuItem>
          )}
        </Menu>
      </DealershipSelector>

      {/* Main Navigation */}
      <Navigation>
        {dynamicNavigationItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);

          return (
            <NavButton
              key={item.name}
              active={active}
              onClick={() => handleNavigation(item.href)}
            >
              <Icon sx={{ fontSize: 20 }} />
              <span>{item.name}</span>
            </NavButton>
          );
        })}

        {/* Admin Section - Only show if admin items are available from API */}
        {dynamicAdminItems.length > 0 && (
        <AdminSection>
            {dynamicAdminItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);

            return (
              <NavButton
                key={item.name}
                active={active}
                onClick={() => handleNavigation(item.href)}
              >
                <Icon sx={{ fontSize: 20 }} />
                <span>{item.name}</span>
              </NavButton>
            );
          })}
        </AdminSection>
        )}
      </Navigation>

      {/* Profile Section */}
      <ProfileSection>
        <ProfileButton onClick={handleProfileClick}>
          <UserAvatar>{userInitials}</UserAvatar>
          <UserInfo>
            <UserName>{userName}</UserName>
            <UserRole>{userRole}</UserRole>
          </UserInfo>
          <ChevronDownIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </ProfileButton>

        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: 240,
              mt: -1,
              ml: 1,
            },
          }}
        >
          <MenuItem onClick={() => handleThemeChange('light')}>
            <ListItemIcon>
              <LightModeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Bright Mode</ListItemText>
            {mode === 'light' && (
              <CheckIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />
            )}
          </MenuItem>
          <MenuItem onClick={() => handleThemeChange('dark')}>
            <ListItemIcon>
              <DarkModeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Dark Mode</ListItemText>
            {mode === 'dark' && (
              <CheckIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />
            )}
          </MenuItem>
          {/* Debug Panel - Only show if not in production */}
          {DEBUG_CONFIG.enabled && (
            <>
              <Divider sx={{ my: 1 }} />
              <MenuItem onClick={() => {
                setDebugPanelOpen(true);
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <BugReportIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Debug Panel</ListItemText>
              </MenuItem>
            </>
          )}
          <Divider sx={{ my: 1 }} />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </ProfileSection>

      {/* Logo at Bottom */}
      <LogoContainer>
        <LogoImage 
          src={mode === 'dark' ? logoWhiteImage : logoBlackImage} 
          alt="myautoIQ" 
        />
      </LogoContainer>

      {/* Debug Panel - Only render if enabled (not in production) */}
      {DEBUG_CONFIG.enabled && (
        <DebugPanelMAIQ
          open={debugPanelOpen}
          onClose={() => setDebugPanelOpen(false)}
        />
      )}
    </SidebarContainer>
  );
};

